// F138e: the floating bar of a real impersonation.
//
// What it replaces was one amber slab reading "Díváš se jako klient X (jen pro
// čtení)". It scrolled away with the page, it named neither the role behind it
// nor how long the hour had left, it could not say that the mode had changed,
// and it never read `staffRestored` - so a partner whose console session had
// expired meanwhile was dropped on a login screen with no explanation.
//
// 🔴 Amber is an ACTION, not a state (DESIGN.md, F138 direction 4). The bar is
// a STATE, so it is a calm elevated surface with a hairline; the one amber
// thing on it is the button that takes the partner back.
import { useCallback, useEffect, useRef, useState } from "react";
import type { AuthMeResponse } from "../shared/dto";
import {
  IMPERSONATION_REFUSALS,
  impersonationRefusalKey,
  type ImpersonationWriteRefusal
} from "../shared/impersonation-writes";
import { READONLY_BLOCKED_EVENT } from "./readonly";
import {
  announceIdentityChange,
  identityFingerprint,
  performImpersonationEnd
} from "./identity-sync";
import "./impersonation.css";

/** How long a named refusal stays on the bar before it steps back. */
const REFUSAL_VISIBLE_MS = 7000;
/**
 * The hour is a hard server limit. Returning a minute BEFORE it means the
 * partner lands back in their console instead of on a login screen: the
 * parent session can only be restored while this session is still valid.
 */
const AUTO_RETURN_LEAD_MS = 60_000;

export type ImpersonationExit = "restored" | "lost";

function refusalKeyOf(value: unknown): string {
  const refusal = IMPERSONATION_REFUSALS.includes(value as ImpersonationWriteRefusal)
    ? (value as ImpersonationWriteRefusal)
    : "other";
  return impersonationRefusalKey(refusal);
}

function remainingLabel(expiresAt: string, now: number): string {
  const minutes = Math.max(0, Math.round((new Date(expiresAt).getTime() - now) / 60_000));
  return String(minutes);
}

export function ImpersonationBar({ data, t, onExit }: {
  data: AuthMeResponse;
  t: (key: string) => string;
  /** Told how the return ended, so the shell can explain a lost console. */
  onExit: (outcome: ImpersonationExit) => void;
}) {
  const impersonation = data.impersonation;
  const [busy, setBusy] = useState<null | "exit" | "mode">(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const exitingRef = useRef(false);

  const exit = useCallback(async (reason: "manual" | "expiring") => {
    // 🔴 A ref claimed SYNCHRONOUSLY, not state: the auto-return timer and a
    // partner's click can land in the same tick, and React lands state on the
    // next render (the F118 lesson).
    if (exitingRef.current) return;
    exitingRef.current = true;
    setBusy("exit");
    // Tabsync: the mounted clock and mode are NOT trusted. The helper asks
    // /me first - the incident's stale tab fired `exit("expiring")` from a
    // mounted hour over a cookie that had been staff for twelve minutes, met
    // 403, and reported an end that never ran.
    const outcome = await performImpersonationEnd(identityFingerprint(data), data.csrfToken);
    if (outcome.kind === "identity-changed") {
      // The cookie already belongs to somebody else: follow it. The reload
      // renders whoever really owns it (console, client, login).
      window.location.reload();
      return;
    }
    if (outcome.kind === "failed") {
      // A refused end with the identity UNCHANGED is a real failure; naming
      // it here beats pretending the console was lost.
      exitingRef.current = false;
      setBusy(null);
      setRefusal("impersonation.endFailed");
      return;
    }
    if (outcome.kind === "offline") {
      // The request never left; the session is still the client's, so say the
      // console could not be restored rather than pretending it was.
      onExit("lost");
      return;
    }
    onExit(outcome.staffRestored ? "restored" : "lost");
    void reason;
  }, [data, onExit]);

  async function switchMode(mode: "look" | "act") {
    setBusy("mode");
    setRefusal(null);
    try {
      const response = await fetch("/api/auth/impersonation/mode", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "X-Launchpad-CSRF": data.csrfToken },
        body: JSON.stringify({ mode })
      });
      if (response.ok) {
        // The mode lives on the session, so the whole app re-reads it: the
        // gate, the bar and every surface must agree on one fact. Other tabs
        // of the same browser share that session - tell them (tabsync).
        announceIdentityChange();
        window.location.reload();
        return;
      }
    } catch {
      /* falls through to the named refusal below */
    }
    setRefusal("impersonation.modeFailed");
    setBusy(null);
  }

  // Every refused write names ITSELF on the bar. One place says it, and it is
  // the place that is on screen the whole time.
  useEffect(() => {
    let timer: number | null = null;
    function onBlocked(event: Event) {
      const detail = (event as CustomEvent<{ refusal?: unknown }>).detail;
      setRefusal(refusalKeyOf(detail?.refusal));
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(() => setRefusal(null), REFUSAL_VISIBLE_MS);
    }
    window.addEventListener(READONLY_BLOCKED_EVENT, onBlocked);
    return () => {
      window.removeEventListener(READONLY_BLOCKED_EVENT, onBlocked);
      if (timer) window.clearTimeout(timer);
    };
  }, []);

  // The clock, and the return that beats it.
  useEffect(() => {
    if (!impersonation) return;
    const tick = window.setInterval(() => {
      const stamp = Date.now();
      setNow(stamp);
      if (new Date(impersonation.expiresAt).getTime() - stamp <= AUTO_RETURN_LEAD_MS) {
        void exit("expiring");
      }
    }, 15_000);
    return () => window.clearInterval(tick);
  }, [impersonation, exit]);

  if (!impersonation) return null;
  const acting = impersonation.mode === "act";

  return (
    <div
      className="impersonation-bar"
      data-impersonation-mode={impersonation.mode}
      role="status"
    >
      <div className="impersonation-identity">
        <span className="impersonation-role">
          {t(`impersonation.role.${impersonation.staffRole}`)}
          {impersonation.staffName ? ` · ${impersonation.staffName}` : ""}
        </span>
        <strong className="impersonation-client">
          {t("impersonation.viewingAs").replace("{client}", impersonation.clientName)}
        </strong>
      </div>

      <span className="impersonation-mode" data-mode={impersonation.mode}>
        {t(acting ? "impersonation.mode.act" : "impersonation.mode.look")}
      </span>

      <span className="impersonation-clock">
        {t("impersonation.remaining").replace("{minutes}", remainingLabel(impersonation.expiresAt, now))}
      </span>

      <div className="impersonation-actions">
        <button
          className="quiet-link"
          data-impersonation-action={acting ? "look" : "act"}
          disabled={busy !== null}
          onClick={() => void switchMode(acting ? "look" : "act")}
          type="button"
        >
          {t(acting ? "impersonation.switchToLook" : "impersonation.switchToAct")}
        </button>
        <button
          className="primary-button impersonation-exit"
          data-impersonation-action="exit"
          disabled={busy === "exit"}
          onClick={() => void exit("manual")}
          type="button"
        >
          {busy === "exit" ? t("impersonation.exiting") : t("impersonation.exit")}
        </button>
      </div>

      {refusal && (
        <p className="impersonation-refusal" role="alert">
          {t(refusal)}
          {refusal === impersonationRefusalKey("looking") && !acting && (
            <>
              {" "}
              <button
                className="quiet-link"
                data-impersonation-action="act-from-refusal"
                disabled={busy !== null}
                onClick={() => void switchMode("act")}
                type="button"
              >
                {t("impersonation.switchToAct")}
              </button>
            </>
          )}
        </p>
      )}
    </div>
  );
}
